/* eslint no-param-reassign:0 */
import { forwardRef } from 'react';

import isUndefinedOrNull from '@zenbusiness/application-commons-utils/isUndefinedOrNull';
import { styled } from '@zenbusiness/zui/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';

const FlexColumnRoot = styled('div', {
  name: 'FlexColumn',
  slot: 'Root'
})`
  display: flex;
  align-self: stretch;
  flex-flow: column nowrap;

  &.FlexColumn-wrap {
    flex-wrap: wrap;
  }

  &.FlexColumn-scrollable {
    overflow-x: hidden;
    overflow-y: auto;
  }

  &.FlexColumn-align-top {
    justify-content: flex-start;
  }

  &.FlexColumn-align-center {
    justify-content: center;
  }

  &.FlexColumn-align-bottom {
    justify-content: flex-end;
  }

  &.FlexColumn-align-between {
    justify-content: space-between;
  }

  &.FlexColumn-align-around {
    justify-content: space-around;
  }

  &.FlexColumn-align-stretch {
    justify-content: stretch;
  }

  &.FlexColumn-justify-left {
    align-items: flex-start;
  }

  &.FlexColumn-justify-center {
    align-items: center;
  }

  &.FlexColumn-justify-right {
    align-items: flex-end;
  }

  &.FlexColumn-justify-stretch {
    align-items: stretch;
  }
`;

const FlexColumn = forwardRef(function FlexColumn(
  {
    justify,
    align,
    className,
    style,
    component,
    scrollable = false,
    wrap = false,
    grow,
    shrink,
    basis,
    auto,
    fill,
    children
  },
  ref
) {
  const Component = component || 'div';

  style = {
    ...style
  };

  if (grow === true) {
    grow = 1;
  }
  if (grow === false) {
    grow = 0;
  }
  if (shrink === true) {
    shrink = 1;
  }
  if (shrink === false) {
    shrink = 0;
  }

  if (fill) {
    style.flex = `1 1 ${basis || 'auto'}`;
  } else {
    if (!isUndefinedOrNull(grow)) {
      style.flexGrow = grow;
    }
    if (!isUndefinedOrNull(shrink)) {
      style.flexShrink = shrink;
    }
    if (basis) {
      style.flexBasis = basis;
    } else if (auto) {
      style.flexBasis = 'auto';
    }
  }

  return (
    <FlexColumnRoot
      as={Component}
      className={clsx(
        className,
        justify && `FlexColumn-justify-${justify}`,
        align && `FlexColumn-align-${align}`,
        scrollable && 'FlexColumn-scrollable',
        wrap && 'FlexColumn-wrap'
      )}
      ref={ref}
      style={style}
    >
      {children}
    </FlexColumnRoot>
  );
});
FlexColumn.propTypes = {
  component: PropTypes.elementType,

  wrap: PropTypes.bool,

  scrollable: PropTypes.bool,

  align: PropTypes.oneOf(['top', 'center', 'bottom', 'between', 'around', 'stretch']),

  justify: PropTypes.oneOf(['left', 'center', 'right', 'stretch']),

  grow: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),

  shrink: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),

  basis: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  auto: PropTypes.bool,

  fill: PropTypes.bool
};

export default FlexColumn;
